/** Настройки расчёта рейтинга MRP (синхронно с server/utils/mrpRatingSettings.js) */

export const MRP_RATING_METRIC_KEYS = [
  'punishments_discord',
  'punishments_as',
  'chat_responses_discord',
  'chat_responses_as',
  'deleted_comments_as',
  'closed_tickets',
];

const defaultMetricRates = () => ({
  punishments_discord: { every: 20, points: 0.5 },
  punishments_as: { every: 20, points: 0.5 },
  chat_responses_discord: { every: 10, points: 0.1 },
  chat_responses_as: { every: 10, points: 0.1 },
  deleted_comments_as: { every: 15, points: 0.1 },
  closed_tickets: { every: 20, points: 0.1 },
});

export const DEFAULT_MRP_RATING_SETTINGS = {
  weekly_norm_regular: 18,
  weekly_norm_senior: 16,
  weekly_punishments_norm_regular: 9,
  weekly_punishments_norm_senior: 8,
  hour_thresholds: [
    { min: 30, points: 1 },
    { min: 55, points: 1 },
    { min: 85, points: 1 },
  ],
  punish_thresholds: [
    { min: 20, points: 1 },
    { min: 45, points: 1 },
  ],
  metric_rates: defaultMetricRates(),
  afk_per_block: 15,
  afk_penalty: 0.5,
  reprimand_penalty: 0.5,
  overtime_hours_step: 5,
  overtime_bonus_per_step: 0.1,
  year_months: 12,
  rating_max: 5,
  notify_telegram_on_change: true,
  chat_response_tracking_enabled: true,
  chat_response_channels: [],
  chat_response_step: 10,
  chat_response_bonus_per_step: 0.1,
  chat_response_require_reply: true,
};

function parseDiscordChannelId(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  let m = s.match(/^<#(\d{17,20})>$/);
  if (m) return m[1];
  m = s.match(/discord(?:app)?\.com\/channels\/\d+\/(\d{17,20})/i);
  if (m) return m[1];
  if (/^\d{17,20}$/.test(s)) return s;
  return null;
}

function normalizeChannelIds(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const item of raw) {
    const id = parseDiscordChannelId(item);
    if (id) out.push(id);
  }
  return [...new Set(out)];
}

let runtimeConfig = null;

function toNum(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeMetricRates(raw) {
  const defaults = defaultMetricRates();
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  MRP_RATING_METRIC_KEYS.forEach((key) => {
    const row = src[key] && typeof src[key] === 'object' ? src[key] : {};
    const fallback = defaults[key];
    out[key] = {
      every: Math.max(0, Math.round(toNum(row.every, fallback.every))),
      points: Math.max(0, toNum(row.points, fallback.points)),
    };
  });
  return out;
}

/** Старый формат «до N на пороге» → прибавки +за каждый порог (только если баллы только росли) */
function thresholdsToIncremental(sorted) {
  if (sorted.length < 2) return sorted;
  const nonDecreasing = sorted.every((t, i) => i === 0 || t.points >= sorted[i - 1].points);
  const hasGrowth = sorted.some((t, i) => i > 0 && t.points > sorted[i - 1].points);
  if (!nonDecreasing || !hasGrowth) return sorted;

  let prev = 0;
  return sorted.map((t) => {
    const inc = Math.max(0, t.points - prev);
    prev = Math.max(prev, t.points);
    return { min: t.min, points: inc };
  });
}

function normalizeThresholds(list, fallback) {
  if (!Array.isArray(list) || !list.length) return [...fallback];
  const sorted = list
    .map((t) => ({
      min: Math.max(0, Math.round(toNum(t.min, 0))),
      points: toNum(t.points, 0),
    }))
    .filter((t) => t.min > 0)
    .sort((a, b) => a.min - b.min);

  const uniq = [];
  for (const t of sorted) {
    if (uniq.length && uniq[uniq.length - 1].min === t.min) {
      uniq[uniq.length - 1] = t;
    } else {
      uniq.push(t);
    }
  }
  if (!uniq.length) return [...fallback];
  return thresholdsToIncremental(uniq);
}

export function normalizeMrpRatingSettings(raw) {
  const d = DEFAULT_MRP_RATING_SETTINGS;
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    weekly_norm_regular: toNum(src.weekly_norm_regular, d.weekly_norm_regular),
    weekly_norm_senior: toNum(src.weekly_norm_senior, d.weekly_norm_senior),
    weekly_punishments_norm_regular: toNum(
      src.weekly_punishments_norm_regular,
      d.weekly_punishments_norm_regular
    ),
    weekly_punishments_norm_senior: toNum(
      src.weekly_punishments_norm_senior,
      d.weekly_punishments_norm_senior
    ),
    hour_thresholds: normalizeThresholds(src.hour_thresholds, d.hour_thresholds),
    punish_thresholds: normalizeThresholds(src.punish_thresholds, d.punish_thresholds),
    metric_rates: normalizeMetricRates(src.metric_rates),
    afk_per_block: Math.max(1, Math.round(toNum(src.afk_per_block, d.afk_per_block))),
    afk_penalty: Math.max(0, toNum(src.afk_penalty, d.afk_penalty)),
    reprimand_penalty: Math.max(0, toNum(src.reprimand_penalty, d.reprimand_penalty)),
    overtime_hours_step: Math.max(1, Math.round(toNum(src.overtime_hours_step, d.overtime_hours_step))),
    overtime_bonus_per_step: Math.max(0, toNum(src.overtime_bonus_per_step, d.overtime_bonus_per_step)),
    year_months: Math.max(1, Math.round(toNum(src.year_months, d.year_months))),
    rating_max: Math.max(1, toNum(src.rating_max, d.rating_max)),
    notify_telegram_on_change:
      src.notify_telegram_on_change === undefined
        ? d.notify_telegram_on_change
        : Boolean(src.notify_telegram_on_change),
    chat_response_tracking_enabled:
      src.chat_response_tracking_enabled === undefined
        ? d.chat_response_tracking_enabled
        : Boolean(src.chat_response_tracking_enabled),
    chat_response_channels: normalizeChannelIds(src.chat_response_channels),
    chat_response_step: Math.max(1, Math.round(toNum(src.chat_response_step, d.chat_response_step))),
    chat_response_bonus_per_step: Math.max(
      0,
      toNum(src.chat_response_bonus_per_step, d.chat_response_bonus_per_step)
    ),
    chat_response_require_reply:
      src.chat_response_require_reply === undefined
        ? d.chat_response_require_reply
        : Boolean(src.chat_response_require_reply),
  };
}

/** Баллы по расценкам метрик: floor(count / every) * points */
export function calcMetricRatePoints(metrics = {}, ratesInput = {}) {
  const rates = normalizeMetricRates(ratesInput);
  const byMetric = {};
  let total = 0;
  MRP_RATING_METRIC_KEYS.forEach((key) => {
    const every = rates[key].every;
    const points = rates[key].points;
    const count = Math.max(0, Number(metrics[key]) || 0);
    const earned = every > 0 && points > 0 ? Math.floor(count / every) * points : 0;
    byMetric[key] = Math.round(earned * 100) / 100;
    total += byMetric[key];
  });
  return { total: Math.round(total * 100) / 100, byMetric };
}

function knotsFromThresholds(thresholds) {
  const knots = [{ min: 0, points: 0 }];
  for (const t of thresholds) {
    knots.push({ min: t.min, points: t.points });
  }
  return knots;
}

function milestonesFromThresholds(thresholds, unit) {
  return thresholds.map((t) => ({
    min: t.min,
    points: t.points,
    label: unit === 'ч' ? `${t.min} ч` : String(t.min),
  }));
}

function scaleThresholds(thresholds, factor) {
  return thresholds.map((t) => ({
    min: Math.round(t.min * factor),
    points: t.points,
  }));
}

export function buildMrpRatingConfig(settings) {
  const s = normalizeMrpRatingSettings(settings);
  const yearMonths = s.year_months;

  const yearHourThresholds = scaleThresholds(s.hour_thresholds, yearMonths);
  const yearPunishThresholds = scaleThresholds(s.punish_thresholds, yearMonths);

  const hoursMax = s.hour_thresholds[s.hour_thresholds.length - 1]?.min || 0;
  const punishMax = s.punish_thresholds[s.punish_thresholds.length - 1]?.min || 0;
  const yearHoursMax = yearHourThresholds[yearHourThresholds.length - 1]?.min || 0;
  const yearPunishMax = yearPunishThresholds[yearPunishThresholds.length - 1]?.min || 0;

  return {
    settings: s,
    ratingMax: s.rating_max,
    yearMonths,
    weeklyNormRegular: s.weekly_norm_regular,
    weeklyNormSenior: s.weekly_norm_senior,
    weeklyPunishmentsNormRegular: s.weekly_punishments_norm_regular,
    weeklyPunishmentsNormSenior: s.weekly_punishments_norm_senior,
    afkPerBlock: s.afk_per_block,
    afkPenalty: s.afk_penalty,
    reprimandPenalty: s.reprimand_penalty,
    overtimeHoursStep: s.overtime_hours_step,
    overtimeBonusPerStep: s.overtime_bonus_per_step,
    metricRates: s.metric_rates,
    chatResponseTrackingEnabled: s.chat_response_tracking_enabled,
    chatResponseChannels: s.chat_response_channels,
    chatResponseStep: s.chat_response_step,
    chatResponseBonusPerStep: s.chat_response_bonus_per_step,
    chatResponseRequireReply: s.chat_response_require_reply,
    month: {
      hourKnots: knotsFromThresholds(s.hour_thresholds),
      punishKnots: knotsFromThresholds(s.punish_thresholds),
      hoursMax,
      punishMax,
      hourMilestones: milestonesFromThresholds(s.hour_thresholds, 'ч'),
      punishMilestones: milestonesFromThresholds(s.punish_thresholds, ''),
    },
    year: {
      hourKnots: knotsFromThresholds(yearHourThresholds),
      punishKnots: knotsFromThresholds(yearPunishThresholds),
      hoursMax: yearHoursMax,
      punishMax: yearPunishMax,
      hourMilestones: milestonesFromThresholds(yearHourThresholds, 'ч'),
      punishMilestones: milestonesFromThresholds(yearPunishThresholds, ''),
    },
  };
}

export function getDefaultMrpRatingConfig() {
  return buildMrpRatingConfig(DEFAULT_MRP_RATING_SETTINGS);
}

export function getMrpRatingConfig() {
  return runtimeConfig || getDefaultMrpRatingConfig();
}

export function applyMrpRatingSettings(raw) {
  runtimeConfig = buildMrpRatingConfig(raw);
}
